import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Building2, Loader2, Plus, Save, Star, Trash2 } from 'lucide-react'
import { useClient, useCreateClient, useUpdateClient } from '@/hooks/queries/useClients'
import { useWasteCategories } from '@/hooks/queries/useWasteCategories'
import { useWasteSubcategories } from '@/hooks/queries/useWasteSubcategories'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { formatRut } from '@/lib/utils'
import type { ClientBranch, ClientFormData } from '@/types'

const createEmptyBranch = (): ClientBranch => ({
  name: '',
  address: '',
  city: '',
  is_primary: false,
})

const INITIAL_FORM: ClientFormData = {
  trade_name: '',
  business_name: '',
  rut: '',
  industry_type: '',
  contact_name: '',
  contact_position: '',
  email: '',
  phone: '',
  branches: [{ ...createEmptyBranch(), is_primary: true }],
  waste_subcategory_ids: [],
}

type FormErrors = Partial<Record<keyof ClientFormData | '_server', string>>
type BranchFieldErrorMap = Partial<Record<number, Partial<Record<'name' | 'address' | 'city' | 'is_primary', string>>>>

function getServerErrors(
  err: unknown
): Partial<Record<keyof ClientFormData, string>> {
  if (!err || typeof err !== 'object' || !('response' in err)) return {}
  const res = (err as { response?: { data?: { errors?: Record<string, string[]> } } })
    .response
  const serverErrors = res?.data?.errors
  if (!serverErrors) return {}
  const mapped: Partial<Record<keyof ClientFormData, string>> = {}
  for (const [key, msgs] of Object.entries(serverErrors)) {
    const normalizedKey = key.startsWith('branches') ? 'branches' : key
    mapped[normalizedKey as keyof ClientFormData] = msgs[0]
  }
  return mapped
}

export function ClientFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const clientId = id ? parseInt(id, 10) : 0

  const [form, setForm] = useState<ClientFormData>(INITIAL_FORM)
  const [errors, setErrors] = useState<FormErrors>({})
  const [branchFieldErrors, setBranchFieldErrors] = useState<BranchFieldErrorMap>({})
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { data: existingClient, isLoading: isLoadingClient } = useClient(clientId)
  const { data: wasteCategories = [] } = useWasteCategories()
  const { data: wasteSubcategories = [] } = useWasteSubcategories()
  const createMutation = useCreateClient()
  const updateMutation = useUpdateClient()

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  useEffect(() => {
    if (isEdit && existingClient) {
      setForm({
        trade_name: existingClient.trade_name,
        business_name: existingClient.business_name,
        rut: existingClient.rut,
        industry_type: existingClient.industry_type ?? '',
        contact_name: existingClient.contact_name ?? '',
        contact_position: existingClient.contact_position ?? '',
        email: existingClient.email ?? '',
        phone: existingClient.phone ?? '',
        branches: existingClient.branches?.length
          ? existingClient.branches.map((branch) => ({
              id: branch.id,
              client_id: branch.client_id,
              name: branch.name,
              address: branch.address,
              city: branch.city,
              is_primary: branch.is_primary,
            }))
          : [{ ...createEmptyBranch(), is_primary: true }],
        waste_subcategory_ids: existingClient.waste_subcategories.map((ws) => ws.id),
      })
    }
  }, [isEdit, existingClient])

  const validate = (): boolean => {
    const newErrors: FormErrors = {}
    const newBranchFieldErrors: BranchFieldErrorMap = {}

    if (!form.trade_name.trim()) {
      newErrors.trade_name = 'El nombre comercial es requerido'
    }
    if (!form.business_name.trim()) {
      newErrors.business_name = 'La razón social es requerida'
    }
    if (!form.rut.trim()) {
      newErrors.rut = 'El RUT es requerido'
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'El correo electrónico no es válido'
    }
    if (!form.waste_subcategory_ids || form.waste_subcategory_ids.length === 0) {
      newErrors.waste_subcategory_ids = 'Debes seleccionar al menos una subcategoría de residuo'
    }

    const cleanedBranches = form.branches.map((branch) => ({
      ...branch,
      name: branch.name.trim(),
      address: branch.address.trim(),
      city: branch.city.trim(),
    }))

    if (cleanedBranches.length === 0) {
      newErrors.branches = 'Debes registrar al menos una sucursal'
    } else {
      cleanedBranches.forEach((branch, index) => {
        const branchErrors: BranchFieldErrorMap[number] = {}
        if (!branch.name) branchErrors.name = 'El nombre es obligatorio'
        if (!branch.address) branchErrors.address = 'La dirección es obligatoria'
        if (!branch.city) branchErrors.city = 'La ciudad es obligatoria'
        if (Object.keys(branchErrors).length > 0) {
          newBranchFieldErrors[index] = branchErrors
        }
      })
    }

    if (Object.keys(newBranchFieldErrors).length > 0) {
      newErrors.branches = 'Corrige los datos obligatorios de las sucursales'
    } else if (cleanedBranches.filter((branch) => branch.is_primary).length !== 1) {
      newErrors.branches = 'Debes marcar una sola sucursal principal'
    }

    setErrors(newErrors)
    setBranchFieldErrors(newBranchFieldErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitError(null)

    if (!validate()) return

    const payload: ClientFormData = {
      ...form,
      industry_type: form.industry_type || undefined,
      contact_name: form.contact_name || undefined,
      contact_position: form.contact_position || undefined,
      email: form.email || undefined,
      phone: form.phone || undefined,
      branches: form.branches.map((branch) => ({
        id: branch.id,
        client_id: branch.client_id,
        name: branch.name.trim(),
        address: branch.address.trim(),
        city: branch.city.trim(),
        is_primary: branch.is_primary,
      })),
    }

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ id: clientId, data: payload })
      } else {
        await createMutation.mutateAsync(payload)
      }
      navigate('/clients')
    } catch (err: unknown) {
      const mapped = getServerErrors(err)
      if (Object.keys(mapped).length > 0) {
        setErrors(mapped)
        const serverErrors = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data?.errors ?? {}
        const nextBranchFieldErrors: BranchFieldErrorMap = {}
        for (const [key, msgs] of Object.entries(serverErrors)) {
          const match = key.match(/^branches\.(\d+)\.(name|address|city|is_primary)$/)
          if (!match) continue
          const [, index, field] = match
          nextBranchFieldErrors[Number(index)] = {
            ...(nextBranchFieldErrors[Number(index)] ?? {}),
            [field]: msgs[0],
          }
        }
        setBranchFieldErrors(nextBranchFieldErrors)
      } else {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data
            ?.message ?? 'Error al guardar el cliente'
        setSubmitError(msg)
      }
    }
  }

  const handleChange = (
    field: Exclude<keyof ClientFormData, 'branches' | 'waste_subcategory_ids'>,
    value: string
  ) => {
    const processed = field === 'rut' ? formatRut(value) : value
    setForm((prev) => ({ ...prev, [field]: processed }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const handleBranchChange = (index: number, field: keyof ClientBranch, value: string | boolean) => {
    setForm((prev) => ({
      ...prev,
      branches: prev.branches.map((branch, branchIndex) => {
        if (field === 'is_primary') {
          return {
            ...branch,
            is_primary: branchIndex === index,
          }
        }

        if (branchIndex !== index) return branch

        return {
          ...branch,
          [field]: value,
        }
      }),
    }))

    if (errors.branches) {
      setErrors((prev) => ({ ...prev, branches: undefined }))
    }
    if (branchFieldErrors[index]?.[field as 'name' | 'address' | 'city' | 'is_primary']) {
      setBranchFieldErrors((prev) => ({
        ...prev,
        [index]: {
          ...prev[index],
          [field]: undefined,
        },
      }))
    }
  }

  const addBranch = () => {
    setForm((prev) => ({
      ...prev,
      branches: [...prev.branches, createEmptyBranch()],
    }))
    if (errors.branches) {
      setErrors((prev) => ({ ...prev, branches: undefined }))
    }
  }

  const removeBranch = (index: number) => {
    setForm((prev) => {
      const nextBranches = prev.branches.filter((_, branchIndex) => branchIndex !== index)
      if (nextBranches.length === 0) {
        return {
          ...prev,
          branches: [{ ...createEmptyBranch(), is_primary: true }],
        }
      }

      if (!nextBranches.some((branch) => branch.is_primary)) {
        nextBranches[0] = { ...nextBranches[0], is_primary: true }
      }

      return {
        ...prev,
        branches: nextBranches,
      }
    })
  }

  const toggleSubcategory = (id: number) => {
    setForm((prev) => {
      const ids = prev.waste_subcategory_ids
      return {
        ...prev,
        waste_subcategory_ids: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
      }
    })
    if (errors.waste_subcategory_ids) {
      setErrors((prev) => ({ ...prev, waste_subcategory_ids: undefined }))
    }
  }

  if (isEdit && isLoadingClient) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#7c8c43]" />
      </div>
    )
  }

  const selectedIds = form.waste_subcategory_ids

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/clients')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {isEdit ? 'Editar cliente' : 'Nuevo cliente'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEdit
              ? `Editando: ${existingClient?.trade_name}`
              : 'Completa el formulario para registrar un nuevo cliente'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {submitError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
            <p className="text-sm text-red-700">{submitError}</p>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Información de la empresa</CardTitle>
            <CardDescription>Datos de identificación del cliente</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="trade_name">
                Nombre comercial <span className="text-red-500">*</span>
              </Label>
              <Input
                id="trade_name"
                value={form.trade_name}
                onChange={(e) => handleChange('trade_name', e.target.value)}
                placeholder="Empresa Fantasia"
                disabled={isSubmitting}
              />
              {errors.trade_name && <p className="text-xs text-red-600">{errors.trade_name}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="business_name">
                Razón social <span className="text-red-500">*</span>
              </Label>
              <Input
                id="business_name"
                value={form.business_name}
                onChange={(e) => handleChange('business_name', e.target.value)}
                placeholder="Empresa Fantasia S.A."
                disabled={isSubmitting}
              />
              {errors.business_name && <p className="text-xs text-red-600">{errors.business_name}</p>}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="rut">
                  RUT <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="rut"
                  value={form.rut}
                  onChange={(e) => handleChange('rut', e.target.value)}
                  placeholder="12.345.678-9"
                  disabled={isSubmitting}
                  maxLength={12}
                />
                {errors.rut && <p className="text-xs text-red-600">{errors.rut}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="industry_type">Tipo de industria</Label>
                <Input
                  id="industry_type"
                  value={form.industry_type}
                  onChange={(e) => handleChange('industry_type', e.target.value)}
                  placeholder="Manufactura, Retail..."
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle className="text-base">Sucursales</CardTitle>
              <CardDescription>Administra las ubicaciones asociadas al cliente</CardDescription>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addBranch} disabled={isSubmitting}>
              <Plus className="mr-2 h-4 w-4" />
              Agregar sucursal
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {form.branches.map((branch, index) => (
              <div key={branch.id ?? `new-${index}`} className="rounded-lg border p-4">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Sucursal {index + 1}</p>
                      <p className="text-xs text-muted-foreground">
                        {branch.is_primary ? 'Sucursal principal' : 'Sucursal secundaria'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant={branch.is_primary ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleBranchChange(index, 'is_primary', true)}
                      disabled={isSubmitting}
                    >
                      <Star className="mr-2 h-4 w-4" />
                      Principal
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeBranch(index)}
                      disabled={isSubmitting || form.branches.length === 1}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Nombre de sucursal</Label>
                    <Input
                      value={branch.name}
                      onChange={(e) => handleBranchChange(index, 'name', e.target.value)}
                      placeholder="Casa Matriz"
                      disabled={isSubmitting}
                    />
                    {branchFieldErrors[index]?.name && (
                      <p className="text-xs text-red-600">{branchFieldErrors[index]?.name}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label>Ciudad</Label>
                    <Input
                      value={branch.city}
                      onChange={(e) => handleBranchChange(index, 'city', e.target.value)}
                      placeholder="Santiago"
                      disabled={isSubmitting}
                    />
                    {branchFieldErrors[index]?.city && (
                      <p className="text-xs text-red-600">{branchFieldErrors[index]?.city}</p>
                    )}
                  </div>

                  <div className="space-y-1.5 md:col-span-2">
                    <Label>Dirección</Label>
                    <Input
                      value={branch.address}
                      onChange={(e) => handleBranchChange(index, 'address', e.target.value)}
                      placeholder="Av. Ejemplo 1234"
                      disabled={isSubmitting}
                    />
                    {branchFieldErrors[index]?.address && (
                      <p className="text-xs text-red-600">{branchFieldErrors[index]?.address}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {errors.branches && <p className="text-xs text-red-600">{errors.branches}</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Contacto</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="contact_name">Nombre de contacto</Label>
                <Input
                  id="contact_name"
                  value={form.contact_name}
                  onChange={(e) => handleChange('contact_name', e.target.value)}
                  placeholder="Juan Pérez"
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="contact_position">Área o cargo</Label>
                <Input
                  id="contact_position"
                  value={form.contact_position}
                  onChange={(e) => handleChange('contact_position', e.target.value)}
                  placeholder="Gerencia de Operaciones"
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email">Correo electrónico</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="contacto@empresa.cl"
                  disabled={isSubmitting}
                />
                {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+56 9 1234 5678"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Subcategorías de residuo <span className="text-red-500">*</span>
            </CardTitle>
            <CardDescription>
              Selecciona las subcategorías de residuo que este cliente genera
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {wasteCategories.length === 0 ? (
              <p className="text-sm text-muted-foreground/70">No hay subcategorías de residuo configuradas.</p>
            ) : (
              wasteCategories.map((cat) => {
                const subs = wasteSubcategories.filter(
                  (s) => s.waste_category_id === cat.id && s.is_active
                )
                if (!subs.length) return null
                return (
                  <div key={cat.id} className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {cat.name}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {subs.map((sub) => {
                        const selected = selectedIds.includes(sub.id)
                        return (
                          <button
                            key={sub.id}
                            type="button"
                            onClick={() => toggleSubcategory(sub.id)}
                            disabled={isSubmitting}
                            className="focus:outline-none"
                          >
                            <Badge
                              variant={selected ? 'default' : 'outline'}
                              className={selected ? 'cursor-pointer' : 'cursor-pointer hover:bg-muted'}
                            >
                              {sub.name}
                            </Badge>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              })
            )}
            {errors.waste_subcategory_ids && (
              <p className="text-xs text-red-600">{errors.waste_subcategory_ids}</p>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-col-reverse gap-3 sm:flex-row">
          <Button type="submit" className="w-full sm:w-auto" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {isEdit ? 'Actualizar cliente' : 'Crear cliente'}
              </>
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() => navigate('/clients')}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
        </div>
      </form>
    </div>
  )
}
